import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X, Users, Download, ArrowUpDown, Settings } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { API_ENDPOINTS } from '../config/api'
import { useAuth } from '../components/AuthProvider'
import { PageLayout } from '../layouts/PageLayout'
import { Button, Select, TextField } from '../components/ui'
import { SearchField } from '../components/ui/SearchField'
import { GovernanceListingSection } from '../components/governance/GovernanceListingSection'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import orgStyles from '@/styles/pages/Organizations.module.scss'
import OrganizationStandardGrantsPanel from '../components/admin/OrganizationStandardGrantsPanel'
import OrganizationDeletionAdminSection from '../components/admin/OrganizationDeletionAdminSection'

type OrganizationUser = {
  id?: string
  name: string
  email: string
  phone: string
  position: string
}

type Organization = {
  id: string | number
  name: string
  domain: string
  country: string
  industry: string
  registrationId: string
  users: OrganizationUser[]
  userCount?: number
  assetCount?: number
  deletionStatus?: 'ACTIVE' | 'PENDING_DELETION'
  deletionScheduledFor?: string | null
  usersLoaded?: boolean
  usersLoadError?: string | null
}

function isPlatformAdminUser(user: { role?: string; permissions?: string[] } | null | undefined) {
  const role = String(user?.role || '').toLowerCase()
  const perms = Array.isArray(user?.permissions) ? user.permissions : []
  return role === 'admin' || perms.includes('admin') || perms.includes('all')
}

function mapOrgRow(row: {
  id: string | number
  name?: string
  userCount?: number
  assetCount?: number
  deletionStatus?: 'ACTIVE' | 'PENDING_DELETION'
  deletionScheduledFor?: string | null
}): Organization {
  return {
    id: row.id,
    name: row.name ?? '',
    domain: '',
    country: '',
    industry: '',
    registrationId: '',
    users: [],
    userCount: row.userCount,
    assetCount: row.assetCount,
    deletionStatus: row.deletionStatus,
    deletionScheduledFor: row.deletionScheduledFor ?? null,
    usersLoaded: false,
    usersLoadError: null,
  }
}

const COUNTRIES = ['Spain', 'Germany', 'Italy', 'Kazakhstan'] as const
const INDUSTRIES = ['Finance', 'Healthcare', 'Manufacturing', 'Technology', 'Cybersecurity', 'Insurance', 'Other'] as const

const STORAGE_KEYS = {
  country: 'orgs_filter_country',
  industry: 'orgs_filter_industry',
  search: 'orgs_search_query',
  sortAsc: 'orgs_sort_asc',
}

type ModalState = {
  name: string
  domain: string
  country: string
  industry: string
  registrationId: string
}

const defaultModalState: ModalState = {
  name: '',
  domain: '',
  country: '',
  industry: '',
  registrationId: '',
}

export default function Organizations() {
  const navigate = useNavigate()
  const { user, token: authToken } = useAuth()
  const isPlatformAdmin = isPlatformAdminUser(user)
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [createError, setCreateError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [expandedId, setExpandedId] = useState<string | number | null>(null)
  const [usersLoadingId, setUsersLoadingId] = useState<string | number | null>(null)
  const [filterCountry, setFilterCountry] = useState<string>('')
  const [filterIndustry, setFilterIndustry] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [sortAsc, setSortAsc] = useState<boolean>(true)

  const [formState, setFormState] = useState<ModalState>(defaultModalState)
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ModalState, string>>>({})
  const [quickAddName, setQuickAddName] = useState('')
  const [quickAddError, setQuickAddError] = useState<string | null>(null)
  const [quickAddSuccess, setQuickAddSuccess] = useState(false)

  const token =
    authToken ||
    (typeof window !== 'undefined' ? localStorage.getItem('token') || localStorage.getItem('accessToken') || null : null)

  const tenantOrganizationsFromAuth = (): Organization[] => {
    const detailed = Array.isArray(user?.organizationsDetailed) ? user!.organizationsDetailed! : []
    if (detailed.length > 0) {
      return detailed
        .filter((o) => o?.id)
        .map((o) => mapOrgRow({ id: String(o.id), name: o.name || String(o.id) }))
    }
    const ids = Array.isArray(user?.organizationIds) ? user!.organizationIds! : []
    const names = Array.isArray(user?.organizations) ? user!.organizations! : []
    if (ids.length > 0) {
      return ids.map((id, i) => mapOrgRow({ id: String(id), name: names[i] || String(id) }))
    }
    if (names.length > 0) {
      return names.map((name, i) => mapOrgRow({ id: `name:${name}`, name: String(name) }))
    }
    return []
  }

  const fetchOrganizations = async () => {
    if (!token) {
      setLoading(false)
      setFetchError('Not authenticated')
      return
    }
    try {
      setLoading(true)
      setFetchError(null)

      // Platform admin: full catalog via admin API. Tenant users: membership only (never elevate).
      if (!isPlatformAdmin) {
        setOrgs(tenantOrganizationsFromAuth())
        return
      }

      const res = await fetch(API_ENDPOINTS.ADMIN_ORGANIZATIONS, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        throw new Error(
          res.status === 403
            ? 'Admin access required to list all organizations'
            : 'Failed to fetch organizations',
        )
      }
      const data = await res.json()
      const list = Array.isArray(data) ? data : data?.organizations ?? []
      setOrgs(list.map((row: Parameters<typeof mapOrgRow>[0]) => mapOrgRow(row)))
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load organizations')
      setOrgs(isPlatformAdmin ? [] : tenantOrganizationsFromAuth())
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchOrganizations()
  }, [isPlatformAdmin, token, user?.id])

  useEffect(() => {
    try {
      const storedCountry = localStorage.getItem(STORAGE_KEYS.country)
      const storedIndustry = localStorage.getItem(STORAGE_KEYS.industry)
      const storedSearch = localStorage.getItem(STORAGE_KEYS.search)
      const storedSort = localStorage.getItem(STORAGE_KEYS.sortAsc)

      if (storedCountry) setFilterCountry(storedCountry)
      if (storedIndustry) setFilterIndustry(storedIndustry)
      if (storedSearch) setSearchQuery(storedSearch)
      if (storedSort !== null) setSortAsc(storedSort === 'true')
    } catch (err) {
      console.warn('[Organizations] Failed to load persisted filters', err)
    }
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.country, filterCountry)
      localStorage.setItem(STORAGE_KEYS.industry, filterIndustry)
      localStorage.setItem(STORAGE_KEYS.search, searchQuery)
      localStorage.setItem(STORAGE_KEYS.sortAsc, String(sortAsc))
    } catch (err) {
      console.warn('[Organizations] Failed to persist filters', err)
    }
  }, [filterCountry, filterIndustry, searchQuery, sortAsc])

  const filteredOrgs = useMemo(() => {
    let list = orgs

    if (filterCountry) list = list.filter((o) => o.country === filterCountry)
    if (filterIndustry) list = list.filter((o) => o.industry === filterIndustry)
    if (searchQuery.trim()) list = list.filter((o) => o.name.toLowerCase().includes(searchQuery.toLowerCase()))

    list = [...list].sort((a, b) => (sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)))

    return list
  }, [orgs, filterCountry, filterIndustry, searchQuery, sortAsc])

  const fetchOrganizationUsers = async (orgId: string | number) => {
    if (!token) return
    setUsersLoadingId(orgId)
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_ORG_USERS(String(orgId)), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) {
        let detail = 'Failed to load organization users'
        try {
          const errBody = await res.json()
          if (errBody?.message) detail = String(errBody.message)
          else if (errBody?.error) detail = String(errBody.error)
        } catch {
          /* ignore */
        }
        throw new Error(detail)
      }
      const data = await res.json()
      const users = Array.isArray(data?.users) ? data.users : []
      setOrgs((prev) =>
        prev.map((org) =>
          String(org.id) === String(orgId)
            ? {
                ...org,
                users: users.map((u: OrganizationUser & { username?: string }) => ({
                  id: u.id,
                  name: u.name || u.username || u.email || '',
                  email: u.email || '',
                  phone: u.phone || '',
                  position: u.position || '',
                })),
                usersLoaded: true,
                usersLoadError: null,
              }
            : org,
        ),
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load users'
      setOrgs((prev) =>
        prev.map((org) =>
          String(org.id) === String(orgId) ? { ...org, usersLoaded: true, usersLoadError: message } : org,
        ),
      )
    } finally {
      setUsersLoadingId(null)
    }
  }

  const handleToggleExpand = (id: string | number) => {
    const willExpand = expandedId !== id
    setExpandedId(willExpand ? id : null)
    if (willExpand) {
      const org = orgs.find((o) => String(o.id) === String(id))
      if (org && !org.usersLoaded) {
        void fetchOrganizationUsers(id)
      }
    }
  }

  const handleSortToggle = () => setSortAsc((prev) => !prev)

  const handleExportCSV = () => {
    const rows = orgs.flatMap((org) => {
      if (!org.users?.length) {
        return [[org.name, org.domain, org.country, org.industry, org.registrationId, '', '', '', '']]
      }
      return org.users.map((u) => [
        org.name,
        org.domain,
        org.country,
        org.industry,
        org.registrationId,
        u.name,
        u.email,
        u.phone,
        u.position,
      ])
    })
    const header = [
      'Organization',
      'Domain',
      'Country',
      'Industry',
      'Registration ID',
      'User Name',
      'Email',
      'Phone',
      'Position',
    ]
    const csv = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'organizations.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleCreateOrganization = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!isPlatformAdmin) {
      setCreateError('Only platform administrators can create organizations')
      return
    }
    const name = formState.name.trim()
    if (!name) {
      setFormErrors((e) => ({ ...e, name: 'Organization name is required' }))
      return
    }
    setFormErrors({})
    if (!token) {
      setCreateError('Not authenticated')
      return
    }
    setCreateError(null)
    try {
      const res = await fetch(API_ENDPOINTS.ADMIN_ORG_NAMES, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok && res.status !== 200) {
        throw new Error(data.message || data.error || 'Failed to create organization')
      }
      setShowModal(false)
      setFormState(defaultModalState)
      setFormErrors({})
      await fetchOrganizations()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create organization')
    }
  }

  const handleQuickAdd = async () => {
    if (!isPlatformAdmin) {
      setQuickAddError('Only platform administrators can create organizations')
      return
    }
    const name = quickAddName.trim()
    if (!name) {
      setQuickAddError('Enter organization name')
      return
    }
    setQuickAddError(null)
    setQuickAddSuccess(false)
    if (!token) {
      setQuickAddError('Not authenticated')
      return
    }
    try {
      const url = API_ENDPOINTS.ADMIN_ORG_NAMES
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok && res.status !== 200) {
        throw new Error(data.message || data.error || 'Failed to create')
      }
      setQuickAddName('')
      setQuickAddSuccess(true)
      await fetchOrganizations()
    } catch (err) {
      setQuickAddError(err instanceof Error ? err.message : 'Failed to create')
    }
  }

  const closeModal = () => {
    setShowModal(false)
    setFormErrors({})
  }

  if (loading && orgs.length === 0) {
    return (
      <PageLayout pageScope="organizations-page" title="Organizations">
        <div className={orgStyles.loadingWrap}>
          <div className={orgStyles.loadingInner}>
            <div className={orgStyles.spinner} aria-hidden />
            <p className={orgStyles.loadingText}>Loading organizations...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      pageScope="organizations-page"
      title="Organizations"
      actions={
        <div className={orgStyles.headerActions}>
          {isPlatformAdmin ? (
            <Button type="button" color="brand" onClick={() => setShowModal(true)} icon={<Plus className={orgStyles.iconSm} aria-hidden />}>
              Create Organization
            </Button>
          ) : null}
          <Button type="button" color="primary" onClick={handleExportCSV} icon={<Download className={orgStyles.iconSm} aria-hidden />} className={orgStyles.btnExport}>
            Export CSV
          </Button>
        </div>
      }
    >
      <div className={listStyles.pageStack}>
        {fetchError && <div className={orgStyles.alertBanner}>{fetchError}</div>}

        {isPlatformAdmin ? (
        <GovernanceListingSection
          title="Quick add"
          description="Create an organization by name only; use Create Organization for full details."
        >
          <form
            className={orgStyles.quickAddRow}
            onSubmit={(e) => {
              e.preventDefault()
              void handleQuickAdd()
            }}
          >
            <div className={orgStyles.quickAddField}>
              <TextField
                label="Organization name"
                placeholder="Organization name"
                value={quickAddName}
                onChange={(v) => {
                  setQuickAddName(v)
                  setQuickAddError(null)
                }}
                minWidth="100%"
              />
            </div>
            <Button type="submit" color="primary" size="small" className={orgStyles.btnAddGreen}>
              Add
            </Button>
            {quickAddError && <span className={orgStyles.inlineMsgError}>{quickAddError}</span>}
            {quickAddSuccess && <span className={orgStyles.inlineMsgOk}>Added. List updated.</span>}
          </form>
        </GovernanceListingSection>
        ) : null}

        <GovernanceListingSection
          title="Organizations"
          description="Search and filter the directory, then open a card to view users."
          toolbar={
            <div className={listStyles.panelToolbarFull}>
              <div className={listStyles.filtersRow}>
                <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                  <SearchField
                    label="Search"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Search organization..."
                    minWidth="14rem"
                  />
                </div>
                <div className={listStyles.toolbarField}>
                  <Select
                    label="Country"
                    options={[{ value: '', label: 'All countries' }, ...COUNTRIES.map((c) => ({ value: c, label: c }))]}
                    value={filterCountry}
                    onChange={(v) => setFilterCountry(v ?? '')}
                    placeholder="Country"
                    minWidth="10rem"
                  />
                </div>
                <div className={listStyles.toolbarField}>
                  <Select
                    label="Industry"
                    options={[{ value: '', label: 'All industries' }, ...INDUSTRIES.map((i) => ({ value: i, label: i }))]}
                    value={filterIndustry}
                    onChange={(v) => setFilterIndustry(v ?? '')}
                    placeholder="Industry"
                    minWidth="10rem"
                  />
                </div>
                <div className={`${listStyles.toolbarField} ${listStyles.toolbarFieldAction}`}>
                  <Button type="button" color="outline" size="small" onClick={handleSortToggle} icon={<ArrowUpDown className={orgStyles.iconSm} aria-hidden />}>
                    Sort {sortAsc ? 'A→Z' : 'Z→A'}
                  </Button>
                </div>
              </div>
            </div>
          }
        >
          {filteredOrgs.length === 0 ? (
            <p className={listStyles.emptyState}>No organizations match current filters.</p>
          ) : (
            <div className={orgStyles.orgGrid}>
              {filteredOrgs.map((org) => (
                <motion.div key={String(org.id)} layout className={orgStyles.orgCard}>
                  <div className={orgStyles.orgName}>{org.name}</div>
                  <div className={orgStyles.orgMeta}>{org.domain}</div>
                  <div className={orgStyles.orgLine}>📍 {org.country}</div>
                  <div className={orgStyles.orgLineMuted}>🏭 {org.industry}</div>
                  <div className={orgStyles.orgLineMuted}>{org.registrationId}</div>

                  {String(org.id).includes('-') ? (
                  <Button
                    type="button"
                    color="outline"
                    size="small"
                    onClick={() =>
                      navigate(`/organization-settings?organization_id=${encodeURIComponent(String(org.id))}`)
                    }
                    icon={<Settings className={orgStyles.iconSm} aria-hidden />}
                    className={orgStyles.viewUsersBtn}
                  >
                    Organization settings
                  </Button>
                  ) : null}

                  <Button
                    type="button"
                    color="outline"
                    size="small"
                    onClick={() => handleToggleExpand(org.id)}
                    icon={<Users className={orgStyles.iconSm} aria-hidden />}
                    className={orgStyles.viewUsersBtn}
                  >
                    {expandedId === org.id
                      ? 'Hide Users'
                      : `View Users${typeof org.userCount === 'number' ? ` (${org.userCount})` : ''}`}
                  </Button>

                  <AnimatePresence>
                    {expandedId === org.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className={orgStyles.userPanel}
                      >
                        {usersLoadingId === org.id ? (
                          <div className={orgStyles.userEmpty}>Loading users...</div>
                        ) : org.usersLoadError ? (
                          <div className={orgStyles.userEmpty}>{org.usersLoadError}</div>
                        ) : org.users.length === 0 ? (
                          <div className={orgStyles.userEmpty}>No users registered</div>
                        ) : (
                          org.users.map((user) => (
                            <div key={user.id || user.email} className={orgStyles.userRow}>
                              <div className={orgStyles.userName}>{user.name}</div>
                              <div className={orgStyles.userEmail}>{user.email}</div>
                              <div className={orgStyles.userEmail}>
                                {user.phone && user.position
                                  ? `${user.phone} — ${user.position}`
                                  : user.position || user.phone || '—'}
                              </div>
                            </div>
                          ))
                        )}
                        {isPlatformAdmin ? (
                          <OrganizationStandardGrantsPanel organizationId={String(org.id)} />
                        ) : null}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </GovernanceListingSection>

        {isPlatformAdmin ? (
          <OrganizationDeletionAdminSection
            organizations={orgs.map((org) => ({
              id: String(org.id),
              name: org.name,
              userCount: org.userCount,
              assetCount: org.assetCount,
              deletionStatus: org.deletionStatus,
              deletionScheduledFor: org.deletionScheduledFor,
            }))}
            onRefresh={fetchOrganizations}
          />
        ) : null}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div className={orgStyles.modalRoot} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div
              className={orgStyles.modalBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
            />
            <motion.div
              className={orgStyles.modalPanel}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <div className={orgStyles.modalHeader}>
                <h2 className={orgStyles.modalTitle}>Create Organization</h2>
                <Button
                  type="button"
                  color="outline"
                  size="small"
                  onClick={closeModal}
                  iconOnly
                  className={orgStyles.modalCloseBtn}
                  aria-label="Close"
                  icon={<X className={orgStyles.iconSm} aria-hidden />}
                >
                  <span className={orgStyles.visuallyHidden}>Close</span>
                </Button>
              </div>

              <form onSubmit={handleCreateOrganization} className={orgStyles.modalForm}>
                {createError && <div className={orgStyles.modalAlert}>{createError}</div>}
                <div className={orgStyles.fieldBlock}>
                  <TextField
                    label="Organization Name"
                    value={formState.name}
                    onChange={(v) => setFormState((prev) => ({ ...prev, name: v }))}
                    minWidth="100%"
                  />
                  {formErrors.name && <p className={orgStyles.fieldError}>{formErrors.name}</p>}
                </div>

                <div className={orgStyles.fieldBlock}>
                  <TextField
                    label="Domain"
                    value={formState.domain}
                    onChange={(v) => setFormState((prev) => ({ ...prev, domain: v }))}
                    minWidth="100%"
                  />
                  {formErrors.domain && <p className={orgStyles.fieldError}>{formErrors.domain}</p>}
                </div>

                <div className={orgStyles.modalGrid2}>
                  <div className={orgStyles.fieldBlock}>
                    <Select
                      label="Country"
                      minWidth="100%"
                      options={[{ value: '', label: 'Select country' }, ...COUNTRIES.map((c) => ({ value: c, label: c }))]}
                      value={formState.country}
                      onChange={(v) => setFormState((prev) => ({ ...prev, country: v ?? '' }))}
                      placeholder="Country"
                    />
                    {formErrors.country && <p className={orgStyles.fieldError}>{formErrors.country}</p>}
                  </div>

                  <div className={orgStyles.fieldBlock}>
                    <Select
                      label="Industry"
                      minWidth="100%"
                      options={[{ value: '', label: 'Select industry' }, ...INDUSTRIES.map((i) => ({ value: i, label: i }))]}
                      value={formState.industry}
                      onChange={(v) => setFormState((prev) => ({ ...prev, industry: v ?? '' }))}
                      placeholder="Industry"
                    />
                    {formErrors.industry && <p className={orgStyles.fieldError}>{formErrors.industry}</p>}
                  </div>
                </div>

                <div className={orgStyles.fieldBlock}>
                  <TextField
                    label="Registration ID"
                    value={formState.registrationId}
                    onChange={(v) => setFormState((prev) => ({ ...prev, registrationId: v }))}
                    minWidth="100%"
                  />
                  {formErrors.registrationId && <p className={orgStyles.fieldError}>{formErrors.registrationId}</p>}
                </div>

                <div className={orgStyles.modalActions}>
                  <Button type="button" color="outline" size="small" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button type="submit" color="brand" size="small">
                    Save Organization
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </PageLayout>
  )
}
